import { prependAttachedConnectors } from "../../../shared/connectors/attachedConnectors";
import { prependAttachedSkills } from "../../../shared/skills/attachedSkills";
import { prependAttachedSteps } from "../../../shared/steps/attachedSteps";
import type { EffortLevel } from "../../../shared/aiProviders";
import {
  type PendingAttachment,
  toAttachmentPayload,
} from "../../lib/attachments";
import type { ChatSendPayload, CodeSnippet, ElementSnippet } from "./chatTypes";

type NamedItem = { name: string };

type BuildSendPayloadOptions = {
  input: string;
  context?: string;
  planMode: boolean;
  effort: EffortLevel | null;
  selectedStep?: string;
  pendingAttachments: PendingAttachment[];
  codeSnippets?: readonly CodeSnippet[];
  elementSnippets?: readonly ElementSnippet[];
  selectedSkills: readonly NamedItem[];
  selectedSteps: readonly NamedItem[];
  selectedConnectors: readonly NamedItem[];
};

type SendableContent = Pick<
  BuildSendPayloadOptions,
  | "input"
  | "pendingAttachments"
  | "codeSnippets"
  | "elementSnippets"
  | "selectedSkills"
  | "selectedSteps"
  | "selectedConnectors"
>;

export function hasSendableContent({
  input,
  pendingAttachments,
  codeSnippets = [],
  elementSnippets = [],
  selectedSkills,
  selectedSteps,
  selectedConnectors,
}: SendableContent): boolean {
  return (
    !!input.trim() ||
    pendingAttachments.length > 0 ||
    codeSnippets.length > 0 ||
    elementSnippets.length > 0 ||
    selectedSkills.length > 0 ||
    selectedSteps.length > 0 ||
    selectedConnectors.length > 0
  );
}

export function displaySnippetPath(filePath: string): string {
  return filePath.split("/").slice(1).join("/");
}

function escapeAttributeQuotes(value: string): string {
  return value.replaceAll('"', "&quot;");
}

function serializeCodeSnippet(snippet: CodeSnippet): string {
  const lines =
    snippet.fromLine === snippet.toLine
      ? `${snippet.fromLine}`
      : `${snippet.fromLine}-${snippet.toLine}`;
  const revision =
    snippet.revision === "previous" ? ` revision="previous"` : "";
  return `<code-context file="${escapeAttributeQuotes(displaySnippetPath(snippet.filePath))}" lines="${lines}"${revision} color="${escapeAttributeQuotes(snippet.color)}">\n${snippet.text}\n</code-context>`;
}

function serializeElementSnippet(snippet: ElementSnippet): string {
  const source = snippet.sourceId
    ? ` source="${escapeAttributeQuotes(snippet.sourceId)}"`
    : "";
  return `<element-context step="${escapeAttributeQuotes(snippet.stepName)}"${source} element="${escapeAttributeQuotes(snippet.descriptor)}" color="${escapeAttributeQuotes(snippet.color)}" index="${snippet.index}">\n<outer-html>\n${snippet.outerHtml}\n</outer-html>\n<text-content>\n${snippet.textContent}\n</text-content>\n</element-context>`;
}

function prependBlocks(text: string, blocks: readonly string[]): string {
  return blocks.length === 0
    ? text
    : blocks.join("\n\n") + (text ? `\n\n${text}` : "");
}

export function buildSendPayload({
  input,
  context,
  planMode,
  effort,
  selectedStep,
  pendingAttachments,
  codeSnippets = [],
  elementSnippets = [],
  selectedSkills,
  selectedSteps,
  selectedConnectors,
}: BuildSendPayloadOptions): ChatSendPayload | null {
  if (
    !hasSendableContent({
      input,
      pendingAttachments,
      codeSnippets,
      elementSnippets,
      selectedSkills,
      selectedSteps,
      selectedConnectors,
    })
  ) {
    return null;
  }

  let text = prependBlocks(input.trim(), context ? [context] : []);
  text = prependBlocks(text, codeSnippets.map(serializeCodeSnippet));
  text = prependBlocks(text, elementSnippets.map(serializeElementSnippet));
  text = prependAttachedConnectors(
    prependAttachedSteps(
      prependAttachedSkills(text, selectedSkills),
      selectedSteps
    ),
    selectedConnectors
  );

  return {
    text,
    selectedStep,
    planMode,
    effort: effort ?? undefined,
    attachments: toAttachmentPayload(pendingAttachments),
  };
}
